import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { delay, map, of, retry, Subscription, switchMap, tap, throwError } from 'rxjs';

import { FileService } from '@ciphr/shared/file-service';

import { FileExportStore } from '../state/file-export.store';
import { ExportJobId } from '../models/export-data.type';

import { FileExportDataAdapter } from '../data-adapters/file-export-data-adapter';
import { FILE_EXPORT_POLLING_TIME } from '../data-adapters/file-export-pulling-time.token';

@Injectable()
export class FileExportService<T> {
  private fileExportDataAdapter: FileExportDataAdapter<T, ExportJobId & Record<string, string>> = inject(
    FileExportDataAdapter<T, ExportJobId & Record<string, string>>,
  );
  private pollingTime = inject(FILE_EXPORT_POLLING_TIME);
  private fileExportStore = inject(FileExportStore);
  private fileService = inject(FileService);

  exportFile(exportName: string, params: T, fileName?: string): void {
    const subscription = new Subscription();
    const delayTime = this.pollingTime;

    subscription.add(
      this.fileExportDataAdapter
        .exportFile(params)
        .pipe(
          tap(({ jobId, ...rest }) => this.fileExportStore.addFile(jobId, exportName, subscription, rest)),
          delay(delayTime),
          switchMap(({ jobId, ...rest }) =>
            this.fileExportDataAdapter.downloadFile(jobId, rest).pipe(
              map((response) => {
                if (response.body?.size) {
                  this.fileExportStore.updateStatus(jobId, 'completed');

                  return response;
                } else {
                  throw 'The file is not ready';
                }
              }),
              retry({
                delay: (error) => {
                  if (error instanceof HttpErrorResponse) {
                    this.fileExportStore.updateStatus(jobId, 'failed');

                    return throwError(() => error.error);
                  } else {
                    return of(void null).pipe(delay(delayTime));
                  }
                },
              }),
            ),
          ),
        )
        .subscribe(({ headers, body }) =>
          fileName ? this.fileService.saveAs(body!, `${fileName}`) : this.fileService.downloadFileFromResponseBlob(headers, body),
        ),
    );
  }
}
